/**
 * GlobalChatBar
 *
 * Mounted once, in the shell layout, so a consistent chat bar is present on
 * every authenticated page — not a per-page embed. It always shows the same
 * layout; only the avatar/name change to say who you're talking to:
 *   - Nothing set yet -> the agency's primary Digital Workforce agent.
 *   - Something set (via useActiveAgent, e.g. "Start Chat" on an agent's
 *     detail page, clicking them in the Digital Team sidebar strip, or the
 *     agent-switcher on the bar's own avatar) -> that specific agent, until
 *     something else changes it.
 *
 * The docked instance is hidden on the chat page (/chat/:id) and on the voice
 * chat page (/assistant), which is a conversation of its own. A brand-new,
 * empty chat there mounts this same bar again with variant="page" — opened
 * full-size in the main panel, agent switcher and all — so New Chat is this
 * chat, just bigger.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/AuthContext';
import { useActiveAgent, type ActiveAgentInfo } from '../../context/ActiveAgentContext';
import { listDwAgents } from '../../features/claudeagents/claudeAgentApi';
import { DW_CONFIGURED_EVENT } from '../../features/digitalworkforce/dwAutoSetup';
import { toActiveAgentInfo, AGENT_AVATAR_NEUTRAL_BG } from '../../utils/agentAvatar';
import { getUploadedAgentPhoto, useAgentPhotosVersion } from '../../utils/agentPhotos';
import { findAreaNameForPath } from '../../constants/centers';
import { ROUTES } from '../../app/routes';
import DwAgentChatBar from './DwAgentChatBar';

interface GlobalChatBarProps {
  variant?: 'dock' | 'page';
  /** Page variant: receives the first message instead of the bar's own
   *  quick chat, along with the agent it was addressed to. */
  onSubmit?: (text: string, files: { name: string; url: string }[], agent: ActiveAgentInfo, apiText: string) => void;
}

const GlobalChatBar: React.FC<GlobalChatBarProps> = ({ variant = 'dock', onSubmit }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const { activeAgent, setActiveAgent, openSignal, chatTarget, setDockReady } = useActiveAgent();
  const [primaryAgent, setPrimaryAgent] = useState<ActiveAgentInfo | null>(null);
  const [agentOptions, setAgentOptions] = useState<ActiveAgentInfo[]>([]);
  // Re-render when an agent's logo is uploaded/removed, and prefer it over
  // whatever photo was captured when the agent was picked.
  useAgentPhotosVersion();
  // Agents picked elsewhere (e.g. the Digital Workforce page) arrive with
  // that page's scene photo and colour — normalise to the small-avatar look:
  // cutout photo (from the agent list) on the neutral tile.
  const withPhoto = (a: ActiveAgentInfo): ActiveAgentInfo => ({
    ...a,
    avatarColor: AGENT_AVATAR_NEUTRAL_BG,
    avatarPhoto: getUploadedAgentPhoto(a.id) ?? agentOptions.find((o) => o.id === a.id)?.avatarPhoto ?? a.avatarPhoto,
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    const load = () => {
      // compact: only id/name/flags are needed, and it is the same request the
      // Command Center / Routines / chat pickers make, so they share one call.
      listDwAgents(false, true).then((records) => {
        const active = records.filter((r) => !r.is_archived);
        setAgentOptions(active.map(toActiveAgentInfo));
        const primary = active.find((r) => r.is_primary_agent) ?? active[0];
        if (primary) setPrimaryAgent(toActiveAgentInfo(primary));
      }).catch(() => {});
    };
    load();
    // A first-time user has no agents (so no bar) until the background setup
    // started at sign-in finishes - load again when it does.
    window.addEventListener(DW_CONFIGURED_EVENT, load);
    return () => window.removeEventListener(DW_CONFIGURED_EVENT, load);
  }, [isAuthenticated]);

  const agent = activeAgent ?? primaryAgent;

  // The full chat page (/chat/:id) is itself the expanded chat experience —
  // a second collapsed pill floating over it would just be a duplicate.
  const onFullChatPage = location.pathname.startsWith('/chat/') || location.pathname === '/chat';
  // The voice chat page is a conversation with the assistant too — a text
  // composer docked under the microphone would be a second way into it.
  const onVoiceChatPage = location.pathname === ROUTES.ASSISTANT || location.pathname.startsWith(`${ROUTES.ASSISTANT}/`);
  const isRateOnly = localStorage.getItem('sourceTool') === 'Rate';

  // What workspace/Center this route belongs to (e.g. "Command Center"), if
  // any — passed through so the agent can see it too, not just the user.
  const areaName = findAreaNameForPath(location.pathname);

  const visible = !!(isAuthenticated && agent && !isRateOnly && !((onFullChatPage || onVoiceChatPage) && variant === 'dock'));
  // Let pages know whether the docked bar is on screen (see ChatTarget).
  useEffect(() => {
    if (variant !== 'dock') return;
    setDockReady(visible);
    return () => setDockReady(false);
  }, [variant, visible, setDockReady]);

  if (!visible || !agent) return null;

  // A page claimed the docked bar as its composer (e.g. a command open in
  // Command Center): same bar, but it posts there and never opens its own
  // quick-chat panel.
  const target = variant === 'dock' ? chatTarget : null;

  return (
    <DwAgentChatBar
      key={agent.id}
      agentId={agent.id}
      agentName={agent.name}
      avatarColor={AGENT_AVATAR_NEUTRAL_BG}
      avatarInitials={agent.avatarInitials}
      avatarPhoto={withPhoto(agent).avatarPhoto}
      openSignal={openSignal}
      areaName={target ? target.label : areaName}
      agentOptions={agentOptions.map(withPhoto)}
      onSwitchAgent={setActiveAgent}
      variant={variant}
      onSubmit={target
        ? (text, _files, mentions) => target.submit(text, mentions)
        : onSubmit ? (text, files, _mentions, apiText) => onSubmit(text, files, agent, apiText) : undefined}
      onOpenAmsPicker={target?.openAmsPicker}
      onUploadFiles={target ? target.uploadFiles : undefined}
      externalBusy={target ? target.busy : undefined}
      externalHasAttachments={target ? target.hasAttachments : undefined}
      externalFiles={target ? target.files : undefined}
      onRemoveExternalFile={target ? target.removeFile : undefined}
    />
  );
};

export default GlobalChatBar;
